import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import CambiarPasswordModal from './CambiarPasswordModal';
import MiPerfilModal       from './MiPerfilModal';
import NotificacionesPanel from './NotificacionesPanel';
import SoporteModal        from './SoporteModal';
import { pushSoportado, getEstadoActual, solicitarPermiso, desuscribir } from '../../lib/pushNotif';
import { soportaBiometria, registrarBiometria } from '../../lib/webauthn';

const ROL_LABEL = {
  admin:      'Administrador',
  supervisor: 'Supervisor',
  maestro:    'Maestro',
  ejecutivo:  'Ejecutivo',
  gerencia:   'Gerencia',
};

const ROL_COLOR = {
  admin:      '#FF6428',
  supervisor: '#3B82F6',
  maestro:    '#10B981',
  ejecutivo:  '#8B5CF6',
  gerencia:   '#F59E0B',
};

export default function MiCuentaMenu({ ejecutivo, logout }) {
  const [open, setOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null); // 'perfil' | 'password' | 'notif' | 'soporte'
  const [noLeidas, setNoLeidas] = useState(0);
  const [ejecutivoLocal, setEjecutivoLocal] = useState(ejecutivo);
  const ref = useRef(null);

  // Sincroniza si cambia el ejecutivo (después de editar perfil)
  useEffect(() => { setEjecutivoLocal(ejecutivo); }, [ejecutivo]);

  // Cargar conteo de notificaciones no leídas
  useEffect(() => {
    if (!ejecutivo?.id) return;
    let cancelado = false;
    async function cargar() {
      const { count } = await supabase
        .from('notificaciones')
        .select('id', { count: 'exact', head: true })
        .eq('destinatario_id', ejecutivo.id)
        .eq('leida', false);
      if (!cancelado) setNoLeidas(count ?? 0);
    }
    cargar();
    // Refrescar cada 60s
    const interval = setInterval(cargar, 60000);
    return () => { cancelado = true; clearInterval(interval); };
  }, [ejecutivo?.id, activeModal]);

  // Cerrar al click fuera / Escape
  useEffect(() => {
    if (!open) return;
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function onEsc(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const nombre = ejecutivoLocal?.nombre || 'Usuario';
  const email  = ejecutivoLocal?.email  || '';
  const rol    = ejecutivoLocal?.rol    || 'ejecutivo';
  const inm    = ejecutivoLocal?.inmobiliaria?.nombre_empresa || '';
  const iniciales = nombre.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();
  const rolColor = ROL_COLOR[rol] || ROL_COLOR.ejecutivo;
  const rolLabel = ROL_LABEL[rol] || rol;

  function abrirModal(name) {
    setActiveModal(name);
    setOpen(false);
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Trigger con badge de no leídas */}
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '4px 10px 4px 4px', border: '0.5px solid var(--border-200)',
          borderRadius: 99, background: 'var(--bg-card)', cursor: 'pointer',
          fontFamily: 'inherit', position: 'relative',
        }}
        title="Mi cuenta">
        <span style={{
          width: 28, height: 28, borderRadius: '50%',
          background: 'var(--bg-muted)', color: 'var(--ink-500)',
          border: '0.5px solid var(--border-200)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 600, fontSize: 11,
        }}>
          {iniciales || '?'}
        </span>
        <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>▾</span>
        {noLeidas > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            background: 'var(--sev-critico, #b94545)', color: 'white',
            fontSize: 10, fontWeight: 700, padding: '2px 5px',
            borderRadius: 99, minWidth: 16, textAlign: 'center', lineHeight: 1.2,
            border: '1.5px solid var(--bg-card)',
          }}>
            {noLeidas > 9 ? '9+' : noLeidas}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          minWidth: 250, background: 'var(--bg-card)',
          border: '0.5px solid var(--border-200)', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          zIndex: 9000, overflow: 'hidden',
        }}>
          {/* Header info */}
          <div style={{ padding: '14px 16px 12px', borderBottom: '0.5px solid var(--border-200)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', marginBottom: 2 }}>{nombre}</div>
            {email && (
              <div style={{ fontSize: 11, color: 'var(--ink-400)', marginBottom: 8, wordBreak: 'break-all' }}>{email}</div>
            )}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{
                display: 'inline-block', padding: '2px 8px', borderRadius: 99,
                background: rolColor + '22', color: rolColor, fontSize: 10,
                fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>{rolLabel}</span>
              {inm && <span style={{ fontSize: 11, color: 'var(--ink-400)' }}>· {inm}</span>}
            </div>
          </div>

          {/* Items */}
          <div style={{ padding: '6px 0' }}>
            <MenuItem icon="👤" label="Mi perfil"  onClick={() => abrirModal('perfil')} />
            <MenuItem icon="🔐" label="Cambiar contraseña" onClick={() => abrirModal('password')} />
            <MenuItem
              icon="🔔"
              label="Notificaciones"
              badge={noLeidas > 0 ? (noLeidas > 9 ? '9+' : String(noLeidas)) : null}
              onClick={() => abrirModal('notif')}
            />
            <BiometriaItem />
            <PushAlertasItem />
            <MenuItem icon="💬" label="Soporte" onClick={() => abrirModal('soporte')} />
          </div>

          {/* Logout */}
          <div style={{ borderTop: '0.5px solid var(--border-200)', padding: '6px 0' }}>
            <MenuItem icon="↩" label="Cerrar sesión" onClick={() => { setOpen(false); logout?.(); }} danger />
          </div>
        </div>
      )}

      {/* Modales */}
      {activeModal === 'perfil' && (
        <MiPerfilModal
          ejecutivo={ejecutivoLocal}
          onClose={() => setActiveModal(null)}
          onActualizado={async () => {
            // recargar el ejecutivo desde BD
            const { data } = await supabase
              .from('ejecutivos')
              .select('*, inmobiliaria:inmobiliarias(nombre_empresa)')
              .eq('id', ejecutivo.id).single();
            if (data) setEjecutivoLocal(data);
          }}
        />
      )}
      {activeModal === 'password' && (
        <CambiarPasswordModal onClose={() => setActiveModal(null)} />
      )}
      {activeModal === 'notif' && (
        <NotificacionesPanel userId={ejecutivo.id} onClose={() => setActiveModal(null)} />
      )}
      {activeModal === 'soporte' && (
        <SoporteModal
          usuario={{
            nombre: ejecutivoLocal?.nombre,
            email: ejecutivoLocal?.email,
            rol: ROL_LABEL[ejecutivoLocal?.rol] || ejecutivoLocal?.rol,
            organizacion: ejecutivoLocal?.inmobiliaria?.nombre_empresa,
          }}
          onClose={() => setActiveModal(null)}
        />
      )}
    </div>
  );
}

function MenuItem({ icon, label, badge, onClick, danger }) {
  const [hover, setHover] = useState(false);
  return (
    <button type="button" onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 16px', border: 'none', cursor: 'pointer',
        background: hover ? 'var(--bg-muted)' : 'transparent',
        color: danger ? 'var(--sev-critico, #b94545)' : 'var(--text-strong)',
        fontSize: 13, textAlign: 'left', fontFamily: 'inherit',
        transition: 'background 0.1s',
      }}>
      <span style={{ fontSize: 14, width: 18, textAlign: 'center' }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {badge && (
        <span style={{
          background: 'var(--sev-critico, #b94545)', color: 'white',
          fontSize: 10, fontWeight: 700, padding: '1px 6px',
          borderRadius: 99, minWidth: 16, textAlign: 'center',
        }}>{badge}</span>
      )}
    </button>
  );
}


// ── Item para activar Face ID / Touch ID / huella / Windows Hello ──────
// La biometría real (cara/huella) NUNCA sale del dispositivo; en el server
// solo guardamos la public key del authenticator. Cumple privacy-first.
function BiometriaItem() {
  const [soporta, setSoporta] = useState(false);
  const [credCount, setCredCount] = useState(0);
  const [pending, setPending] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    setSoporta(soportaBiometria());
  }, []);

  // Cargar cantidad de credentials del usuario (RLS self-read permite esto).
  const cargar = React.useCallback(async () => {
    const { count } = await supabase
      .from('webauthn_credentials')
      .select('id', { count: 'exact', head: true });
    setCredCount(count ?? 0);
  }, []);

  useEffect(() => {
    if (soporta) cargar();
  }, [soporta, cargar]);

  if (!soporta) return null;

  async function activar() {
    if (pending) return;
    setPending(true);
    setFeedback(null);
    const r = await registrarBiometria();
    if (r.ok) {
      setFeedback({ tipo: 'ok', msg: `${r.device_name} activado ✓` });
      cargar();
      // Auto-clear después de 3.5s solo en éxito.
      setTimeout(() => setFeedback(null), 3500);
    } else {
      setFeedback({ tipo: 'err', msg: r.error || 'No se pudo activar' });
      // En error, NO auto-clear: que el usuario lea el detalle y decida.
    }
    setPending(false);
  }

  const label = pending
    ? 'Activando…'
    : credCount > 0
      ? `Biometría activa · ${credCount} dispositivo${credCount > 1 ? 's' : ''}`
      : 'Activar Face ID / Touch ID';

  return (
    <>
      <MenuItem
        icon={credCount > 0 ? '🔓' : '👁️'}
        label={label}
        onClick={activar}
      />
      {feedback && (
        <div style={{
          padding: '8px 16px 10px', fontSize: 11.5,
          color: feedback.tipo === 'ok' ? '#27500a' : '#991b1b',
          background: feedback.tipo === 'err' ? 'rgba(220, 38, 38, 0.06)' : 'transparent',
          borderTop: feedback.tipo === 'err' ? '0.5px solid rgba(220, 38, 38, 0.2)' : 'none',
          lineHeight: 1.45,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <span>{feedback.msg}</span>
            {feedback.tipo === 'err' && (
              <button
                type="button"
                onClick={() => setFeedback(null)}
                style={{ background: 'none', border: 0, color: '#991b1b', fontSize: 11, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
              >
                <X size={16} strokeWidth={1.75} aria-hidden style={{verticalAlign:"-3px",display:"inline-block"}} />
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ── Item para activar/desactivar push notifications (PWA) ──────────────
function PushAlertasItem() {
  const [estado, setEstado] = useState(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    let cancelado = false;
    getEstadoActual().then(s => { if (!cancelado) setEstado(s); });
    return () => { cancelado = true; };
  }, []);

  if (!estado || !estado.soportado) return null;

  const activo = estado.permiso === 'granted' && estado.suscrito;
  const bloqueado = estado.permiso === 'denied';

  async function toggle() {
    if (pending) return;
    setPending(true);
    try {
      if (activo) {
        await desuscribir();
        setEstado(s => ({ ...s, suscrito: false }));
      } else {
        if (bloqueado) {
          alert('Las notificaciones están bloqueadas. Desbloqueá el sitio en la configuración del navegador y recarga.');
          return;
        }
        await solicitarPermiso();
        setEstado({ soportado: true, permiso: 'granted', suscrito: true });
      }
    } catch (e) {
      alert(e.message || 'No se pudo cambiar el estado de las alertas.');
    } finally {
      setPending(false);
    }
  }

  return (
    <MenuItem
      icon={activo ? '📱' : '🔕'}
      label={pending ? 'Procesando…' : activo ? 'Alertas push activas · click para desactivar' : 'Activar alertas en este dispositivo'}
      onClick={toggle}
    />
  );
}
